/** Ряд по дням карточки оффера (`mart.wb_region_supply_demand`): Σ штук по столбцам регионов
 *  (страны — «Зарубеж») и заказов за день по дням — только суммы штук (правило 3: окна 7/28 по
 *  дням не суммируются, их тут нет). null ≠ 0: из одних null — null (на графике — разрыв).
 *  Разбивка FBO дня — надёжно (known_chain + by_supply_signal) и слабо (weak + unresolved), всего и
 *  по столбцу региона (подсказка графика). Неполный день (`orders_complete` / `sales_complete` =
 *  false хоть в одной строке) — помечается. */
import type { WbHistoryRow } from "@/api/wbRegions";
import { columnKey } from "./matrix";

export interface HistoryPoint {
  date: string;
  /** подпись оси: «дд.мм», неполный день — со «*» */
  label: string;
  /** FBO по столбцу региона (ключ — `columnKey`) */
  fbo: Record<string, number | null>;
  /** слабая часть FBO (weak + unresolved) по столбцу региона */
  fbo_weak_by: Record<string, number | null>;
  /** FBO дня: надёжная часть (known_chain + by_supply_signal) и слабая (weak + unresolved) */
  fbo_reliable: number | null;
  fbo_weak: number | null;
  orders_fbo: number | null;
  orders_fbs: number | null;
  sales_qty: number | null;
  orders_incomplete: boolean;
  sales_incomplete: boolean;
}

function add(a: number | null, b: number | null): number | null {
  if (b === null) return a;
  return (a ?? 0) + b;
}

export function historyPoints(rows: WbHistoryRow[]): { points: HistoryPoint[]; keys: string[] } {
  const byDate = new Map<string, HistoryPoint>();
  const keys: string[] = [];
  for (const r of rows) {
    const k = columnKey(r);
    if (k === null) throw new Error(`регион ${String(r.region_wb)} вида ${String(r.region_kind)} вне контракта`);
    if (!keys.includes(k)) keys.push(k);
    let p = byDate.get(r.date);
    if (!p) {
      p = {
        date: r.date,
        label: "",
        fbo: {},
        fbo_weak_by: {},
        fbo_reliable: null,
        fbo_weak: null,
        orders_fbo: null,
        orders_fbs: null,
        sales_qty: null,
        orders_incomplete: false,
        sales_incomplete: false,
      };
      byDate.set(r.date, p);
    }
    p.fbo[k] = add(p.fbo[k] ?? null, r.fbo_stock);
    const weak = add(r.fbo_stock_weak, r.fbo_stock_unresolved);
    p.fbo_weak_by[k] = add(p.fbo_weak_by[k] ?? null, weak);
    p.fbo_weak = add(p.fbo_weak, weak);
    p.fbo_reliable = add(p.fbo_reliable, add(r.fbo_stock_known_chain, r.fbo_stock_by_supply_signal));
    p.orders_fbo = add(p.orders_fbo, r.orders_fbo);
    p.orders_fbs = add(p.orders_fbs, r.orders_fbs);
    p.sales_qty = add(p.sales_qty, r.sales_qty);
    if (r.orders_complete === false) p.orders_incomplete = true;
    if (r.sales_complete === false) p.sales_incomplete = true;
  }
  const points = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  for (const p of points) {
    p.label = `${p.date.slice(8, 10)}.${p.date.slice(5, 7)}${p.orders_incomplete || p.sales_incomplete ? "*" : ""}`;
  }
  return { points, keys };
}

/** Текст неполноты дня: «заказы неполные, продажи неполные». */
export function incompleteText(p: HistoryPoint): string {
  return [p.orders_incomplete && "заказы неполные", p.sales_incomplete && "продажи неполные"]
    .filter(Boolean)
    .join(", ");
}
